import { useViewMode } from '@/shared/hooks/useViewMode';
import { Avatar, Chip, StatusDot } from '@/shared/ui/atoms';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { SortPicker } from '@/features/project-board/components/SortPicker';
import { DateSort, type SortKey } from '@/features/project-board/sorting';
import { useBoard } from '@/features/project-board/BoardProvider';
import type { DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { DrawerPayloads } from '@/features/project-board/drawer-payloads';
import { INCIDENT_STAGE_COLUMNS, type Incident } from '@/features/project-board/types';
import { ViewFrame } from '@/features/project-board/views/ViewFrame';
import { ItemKey } from '@/features/project-board/identity';
import { OwnerRoster } from '@/features/project-board/owners';

/**
 * 처리자 표시 — 담당자 필터와 같은 판정(`memberOf`)을 거친 이름을 우선 쓴다. `IssueView`의
 * `handlerOf`와 같은 이유 — 필터는 명부 기준 이름으로 좁히는데 화면에 커밋 author 원문을 그대로
 * 실으면 담당자를 골라도 표기가 달라 안 걸러진 것처럼 보인다.
 */
function handlerOf(incident: Pick<Incident, 'handledBy'>): string {
  return OwnerRoster.memberOf(incident.handledBy) || incident.handledBy;
}

const EDGE: Record<string, string> = {
  todo: 'border-l-todo',
  progress: 'border-l-progress',
  done: 'border-l-done',
};

const STATUS_LABEL: Record<string, string> = {
  todo: '대기',
  progress: '진행 중',
  done: '완료',
};

type IncidentViewMode = 'stage' | 'table';

const VIEW_MODES: ReadonlyArray<ViewModeOption<IncidentViewMode>> = [
  { key: 'stage', label: '단계', glyph: '▤' },
  { key: 'table', label: '테이블', glyph: '☰' },
];

const SORT_OPTIONS = DateSort.options('발생일');

export function IncidentView(): React.JSX.Element {
  const { incidents: scoped } = useBoard();
  const [mode, setMode] = useViewMode<IncidentViewMode>('project-board.incident.view', 'stage');
  const [sort, setSort] = useViewMode<SortKey>('project-board.incident.sort', 'default');

  // 인시던트의 등록 시점은 발생(제보) 시각이다. 같은 날은 id로 순서를 고정한다.
  const incidents = DateSort.sort(scoped, sort, (incident) => incident.reportedAt, (incident) => incident.id);

  return (
    <ViewFrame
      scope="incidents"
      countLabel={`${incidents.length}개 인시던트`}
      isEmpty={incidents.length === 0}
      emptyMessage="조건에 맞는 인시던트가 없다."
      toolbar={
        <div className="flex items-center gap-2">
          <SortPicker options={SORT_OPTIONS} value={sort} onChange={setSort} label="인시던트 정렬" />
          <ViewModeToggle options={VIEW_MODES} value={mode} onChange={setMode} />
        </div>
      }
      render={(open) =>
        mode === 'table' ? (
          <IncidentTable incidents={incidents} open={open} />
        ) : (
          <StageBoard incidents={incidents} open={open} />
        )
      }
    />
  );
}

/** 인시던트는 상태가 아니라 **재발 방지 단계**가 컬럼이다 — 접수→문서 리뷰→task 분해→실행→리뷰→E2E. */
function StageBoard({
  incidents,
  open,
}: {
  incidents: Incident[];
  open: (payload: DrawerPayload) => void;
}): React.JSX.Element {
  return (
    <div className="grid grid-flow-col auto-cols-[minmax(280px,1fr)] items-start gap-3 overflow-x-auto pb-2">
      {INCIDENT_STAGE_COLUMNS.map((column) => {
        const items = incidents.filter((incident) => incident.stage === column.key);
        return (
          <div key={column.key} className="min-w-[280px] rounded-lg border border-line-soft bg-[#f0f3f6]">
            <div className="flex items-center gap-1.5 border-b border-line-soft px-3 py-2.5 text-xs font-semibold text-muted">
              {column.label}
              <span className="ml-auto rounded-full bg-surface px-1.5 text-[11px]">{items.length}</span>
            </div>
            <div className="flex max-h-[calc(100vh-230px)] flex-col gap-2 overflow-y-auto p-2.5">
              {items.map((incident) => (
                <article
                  key={ItemKey.incident(incident)}
                  title={incident.title}
                  onClick={() => open(DrawerPayloads.incident(incident))}
                  className={`cursor-pointer rounded-md border border-line border-l-[3px] bg-surface p-2.5 hover:border-accent ${EDGE[incident.status]}`}
                >
                  <div className="line-clamp-3 text-[13px] leading-snug">{incident.titleHead}</div>
                  {incident.titleDetail && (
                    <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted">
                      {incident.titleDetail}
                    </div>
                  )}
                  <footer className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-[11px] text-muted">{incident.reportedAt || incident.id}</span>
                    {incident.repos.slice(0, 2).map((repo) => (
                      <Chip tone="repo" key={repo}>
                        {repo}
                      </Chip>
                    ))}
                    {incident.skippedStages.length > 0 && (
                      <Chip tone="warn">⚠ {incident.skippedStages.join(',')} 없음</Chip>
                    )}
                    <span className="flex-1" />
                    {incident.handledBy && <Avatar name={handlerOf(incident)} />}
                  </footer>
                </article>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 테이블 뷰 — 단계 보드가 "어디까지 갔나"를 보는 화면이라면 이건 **줄 세워 비교하는 화면**이다.
 * 카드에서 잘리던 제목·repo를 다 보여주고, 단계 보드에서는 컬럼 위치로만 알던 단계를 값으로 세운다.
 */
function IncidentTable({
  incidents,
  open,
}: {
  incidents: Incident[];
  open: (payload: DrawerPayload) => void;
}): React.JSX.Element {
  return (
    // 이 래퍼에 overflow를 주면 thead sticky 기준이 래퍼로 바뀌어 헤더가 첫 행 뒤로 밀린다 — 주지 않는다.
    <div className="rounded-lg border border-line bg-surface">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col className="w-[100px]" />
          <col />
          <col className="w-[132px]" />
          <col className="w-[88px]" />
          <col className="w-[170px]" />
          <col className="w-[128px]" />
          <col className="w-[96px]" />
        </colgroup>
        <thead>
          <tr className="[&>th]:sticky [&>th]:top-topbar [&>th]:z-10 [&>th]:bg-[#f0f3f6] [&>th]:px-2.5 [&>th]:py-2.5 [&>th]:text-left [&>th]:align-middle [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted [&>th]:shadow-[inset_0_-1px_0_var(--color-line)]">
            <th>발생일</th>
            <th>인시던트</th>
            <th>단계</th>
            <th>상태</th>
            <th>Repo</th>
            <th>처리자</th>
            <th>갱신</th>
          </tr>
        </thead>
        <tbody>
          {incidents.map((incident) => (
            <tr
              key={ItemKey.incident(incident)}
              onClick={() => open(DrawerPayloads.incident(incident))}
              className="cursor-pointer border-b border-line-soft last:border-0 hover:bg-canvas [&>td]:px-2.5 [&>td]:py-2.5 [&>td]:align-top"
            >
              <td className="font-mono text-[11.5px] whitespace-nowrap text-muted">{incident.reportedAt || '-'}</td>
              <td>
                <div className="text-[13.5px] leading-snug font-semibold">{incident.titleHead}</div>
                {incident.titleDetail && (
                  <div className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-muted">
                    {incident.titleDetail}
                  </div>
                )}
                <div className="mt-0.5 truncate font-mono text-[11px] text-faint">{incident.slug}</div>
              </td>
              <td>
                <div className="text-[12px] font-semibold">{incident.stageLabel}</div>
                {incident.skippedStages.length > 0 && (
                  <div className="mt-0.5 text-[11px] text-amber-800" title={incident.skippedStages.join(', ')}>
                    ⚠ {incident.skippedStages.length}단계 건너뜀
                  </div>
                )}
              </td>
              <td className="text-[12px] whitespace-nowrap">
                <StatusDot status={incident.status} />
                {STATUS_LABEL[incident.status] ?? incident.status}
              </td>
              <td>
                <div className="flex flex-wrap gap-1">
                  {incident.repos.length > 0 ? (
                    incident.repos.map((repo) => (
                      <Chip tone="repo" key={repo}>
                        {repo}
                      </Chip>
                    ))
                  ) : (
                    <span className="text-[11px] text-faint">-</span>
                  )}
                </div>
              </td>
              <td>
                {incident.handledBy ? (
                  <span className="flex items-center gap-1.5 text-[11.5px] text-muted">
                    <Avatar name={handlerOf(incident)} />
                    <span className="truncate">{handlerOf(incident)}</span>
                  </span>
                ) : (
                  <span className="text-[11px] text-faint">-</span>
                )}
              </td>
              <td className="text-[11px] whitespace-nowrap text-muted">{incident.updatedAt ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
